import * as i4 from '@upr-web-primer/app-utils';
import { paramNum, param, paramBool, DEFAULT_RETRY_STRATEGY, genericRetryStrategy, AutoSubscription, AppEnvironmentService } from '@upr-web-primer/app-utils';
import { __decorate, __metadata } from 'tslib';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import * as i2 from '@upr-web-primer/spinner';
import { SPINNER_KEYS, SpinnerService } from '@upr-web-primer/spinner';
import * as i3 from '@upr-web-primer/storage';
import { LocalStorageService } from '@upr-web-primer/storage';
import { WINDOW } from '@upr-web-primer/window';
import { merge, find, get } from 'lodash-es';
import { firstValueFrom, timer, throwError } from 'rxjs';
import { retry, delay, catchError } from 'rxjs/operators';
const OIDC_REPLACED_DEFAULTS = {
  oidc: {
    refresh_expires_in_seconds: paramNum('$oidc.refresh_expires_in_seconds$'),
    app_flow_path: param('$oidc.app_flow_path$'),
    authorize_enabled: paramBool('$oidc.authorize_enabled$'),
    authorize_path: param('$oidc.authorize_path$'),
    base_url: param('$oidc.base_url$'),
    base_url_secondary: param('$oidc.base_url_secondary$'),
    client_id: param('$oidc.client_id$'),
    client_secret: param('$oidc.client_secret$'),
    enabled: paramBool('$oidc.enabled$'),
    end_session: param('$oidc.end_session$'),
    node_api_base_url: param('$oidc.node_api_base_url$'),
    password_flow_path: param('$oidc.password_flow_path$'),
    redirect_enabled: paramBool('$oidc.redirect_enabled$'),
    redirect_url: param('$oidc.redirect_url$')
  }
};
const OIDC_ENV_DEFAULTS = {
  oidc: {
    refresh_expires_in_seconds: 86400,
    app_flow_path: '/oidc/connect/token',
    authorize_enabled: false,
    authorize_path: '/account/api/oidc/authorize',
    base_url: '',
    base_url_secondary: '',
    client_id: '',
    client_secret: '',
    enabled: true,
    end_session: '/oidc/connect/endsession',
    node_api_base_url: '/account/api/oidc',
    password_flow_path: '/oidc/connect/token',
    redirect_enabled: false,
    redirect_url: '/oidc/connect/authorize'
  }
};
let OidcService = class OidcService {
  constructor(window, httpClient, spinner, localStorage, appEnvService) {
    this.window = window;
    this.httpClient = httpClient;
    this.spinner = spinner;
    this.localStorage = localStorage;
    this.appEnvService = appEnvService;
    this.cache = new Map();
    this.spinnerToken = 'LoginOIDC';
    this.defaultState = 'web';
    this.appEnvService.addDefaultValues(OIDC_ENV_DEFAULTS, OIDC_REPLACED_DEFAULTS);
    this.isLocalhost = new RegExp(/localhost|web-local/, 'gi').test(this.window.document.location.hostname);
    this.subscriptions.add(this.appEnvService.envValues$.subscribe(values => {
      this.envConfig = values['oidc'];
      if (this.isLocalhost && (this.envConfig.base_url || '') !== '/api') {
        //verify if path is already set in the environment loader for containerized app.
        const isApiBaseUrlAlreadySet = !this.envConfig.base_url.includes('http');
        this.envConfig.base_url = this.window.document.location.origin + (isApiBaseUrlAlreadySet ? this.envConfig.base_url : '/api'); // this is for localhost
      }
    }));
    this.config = {
      retry: 4,
      retry_delay: 500,
      headers: {}
    };
  }
  /**
   * @desc Global config settings, currently only supports 2 flows
   * Application Flow and Password Flow
   * @param configurations sets configuration for Oauth Flows, see Configuration Interface
   */
  setConfig(configurations) {
    this.config = merge({}, this.config, configurations);
  }
  /**
   * @desc gets all configurations that were set during runtime
   * @returns configurations
   */
  getConfig() {
    return this.config;
  }
  get token() {
    return this._token;
  }
  set token(theToken) {
    this._token = theToken;
  }
  /**
   * Gets app token
   * @returns app token
   */
  getAppToken() {
    const cacheKey = 'APP_TOKEN';
    const checkCache = this.cache.has(cacheKey);
    if (!checkCache) {
      const postData = `grant_type=client_credentials&scope=default`;
      const options = {
        headers: {
          Authorization: `Basic ` + this.window.btoa(`${this.envConfig.client_id}:${this.envConfig.client_secret}`),
          Accept: `application/json`,
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      };
      // if any additional headers are passed, merge them
      merge(options.headers, this.config.headers);
      const promise = firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options).pipe(retry(this.config.retry), delay(this.config.retry_delay), catchError(this.handleError))).finally(() => {
        firstValueFrom(timer(500)).then(() => {
          this.cache.delete(cacheKey);
        });
      });
      this.cache.set(cacheKey, promise);
      return promise;
    }
    return this.cache.get(cacheKey);
  }
  /**
   * Gets auth token
   * @returns auth token
   */
  getAuthToken() {
    if (!this.config.username || !this.config.password) {
      throw new Error('To use Password Flow, you must provide username and password');
    }
    const postData = `grant_type=password&scope=default openid offline_access&username=${this.config.username}&password=${this.config.password}`;
    const options = {
      headers: {
        Authorization: `Basic ` + this.window.btoa(`${this.envConfig.client_id}:${this.envConfig.client_secret}`),
        Accept: `application/json`,
        'Cache-Control': 'no-cache',
        username: this.config.username,
        'subj-claim': 'default',
        // required, currently unused
        'aud-claim': 'default',
        // required, currently unused
        'iss-claim': 'default',
        // required, currently unused
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    };
    // if any additional headers are passed, merge them
    merge(options.headers, this.config.headers);
    const customRetry = DEFAULT_RETRY_STRATEGY;
    customRetry.customFunc = (error, retryObj, attempt) => {
      if (error?.error === 'invalid_client' || error?.error?.error === 'invalid_client') {
        return throwError(() => error);
      }
      return timer(attempt * retryObj.scalingDuration);
    };
    const retryStrategy = genericRetryStrategy(customRetry);
    return firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options).pipe(retry(retryStrategy), catchError(this.errorHandler)));
  }
  /**
   * Gets refresh token
   * @param currentToken
   * @returns refresh token
   */
  getRefreshToken(currentToken) {
    const cacheKey = 'REFRESH_TOKEN';
    const checkCache = this.cache.has(cacheKey);
    if (!checkCache) {
      const postData = `grant_type=refresh_token&scope=openid default offline_access&refresh_token=${currentToken}`;
      const options = {
        headers: {
          Authorization: 'Basic ' + this.window.btoa(this.envConfig.client_id + ':' + this.envConfig.client_secret),
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      };
      // if any additional headers are passed, merge them
      merge(options.headers, this.config.headers);
      const promise = firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)).finally(() => {
        firstValueFrom(timer(500)).then(() => {
          this.cache.delete(cacheKey);
        });
      });
      this.cache.set(cacheKey, promise);
      return promise;
    }
    return this.cache.get(cacheKey);
  }
  getTokensByCode(currentCode, redirectUri) {
    const cacheKey = 'AUTHORIZATION_TOKENS';
    const checkCache = this.cache.has(cacheKey);
    if (!checkCache) {
      const postData = `grant_type=authorization_code&code=${currentCode}&redirect_uri=${redirectUri}`;
      const options = {
        headers: {
          Authorization: 'Basic ' + this.window.btoa(this.envConfig.client_id + ':' + this.envConfig.client_secret),
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      };
      // if any additional headers are passed, merge them
      merge(options.headers, this.config.headers);
      const promise = firstValueFrom(this.httpClient.post(this.envConfig.base_url + this.envConfig.app_flow_path, postData, options)).finally(() => {
        firstValueFrom(timer(500)).then(() => {
          this.cache.delete(cacheKey);
        });
      });
      this.cache.set(cacheKey, promise);
      return promise;
    }
    return this.cache.get(cacheKey);
  }
  /**
   * Logins oidc service
   * @param username
   * @param password
   * @param redirectUri
   * @param is_corp
   * @returns login
   */
  Login(username, password, redirectUri, is_corp) {
    if (!username || !password || !redirectUri) {
      return Promise.reject('invalid params');
    }
    this.spinner.show(this.spinnerToken);
    return this.getLoginCsrefToken(is_corp).then(_csrf => {
      let httpHeaders = new HttpHeaders();
      httpHeaders = this.setNoCacheHeaders(httpHeaders);
      httpHeaders = httpHeaders.append('Accept', 'text/html');
      httpHeaders = httpHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
      httpHeaders = httpHeaders.append('enctype', 'multipart/form-data');
      const bodyRequest = `scope=openid default&grant_type=password&username=${username}&password=${password}&client_id=${this.envConfig.client_id}&state=${_csrf}&redirect_uri=${redirectUri}&nonce=${this.uid()}&is_corp=${this.isCorpString(is_corp)}`;
      return firstValueFrom(this.httpClient.post(this.envConfig.app_flow_path, bodyRequest, {
        headers: httpHeaders,
        responseType: 'text',
        observe: 'response'
      })).then(response => {
        const oidcResponse = this.getOidcResponse(response.url);
        this.spinner.hide(this.spinnerToken);
        return Promise.resolve(oidcResponse);
      }).catch(error => this.errorHandler(error));
    });
  }
  /**
   * Logins by id token
   * @param idToken
   * @param [redirectUri]
   * @param is_corp
   * @returns by id token
   */
  LoginByIdToken(idToken, redirectUri, is_corp) {
    const url = `${this.envConfig.app_flow_path}?response_type=id_token token&scope=openid default&client_id=${this.envConfig.client_id}&redirect_uri=${redirectUri || this.window.location.href}&nonce=${this.uid()}&state=${this.defaultState}+&id_token_hint=${idToken}&is_corp=${this.isCorpString(is_corp)}`;
    let httpHeaders = new HttpHeaders();
    httpHeaders = this.setNoCacheHeaders(httpHeaders);
    httpHeaders = httpHeaders.append('Accept', 'text/html');
    const httpOptions = {
      headers: httpHeaders,
      responseType: 'text',
      observe: 'response'
    };
    return firstValueFrom(this.httpClient.get(url, httpOptions)).then(response => {
      const oidcResponse = this.getOidcResponse(response.url);
      return Promise.resolve(oidcResponse);
    }).catch(error => this.errorHandler(error));
  }
  /**
   * Gets login csref token
   * @param is_corp
   * @returns login csref token
   */
  getLoginCsrefToken(is_corp) {
    const url = `${this.envConfig.app_flow_path}?response_type=id_token token&scope=openid default&client_id=${this.envConfig.client_id}&redirect_uri=${this.window.location.href}&nonce=${this.uid()}&state=${this.defaultState}&is_corp=${this.isCorpString(is_corp)}`;
    let httpHeaders = new HttpHeaders();
    httpHeaders = this.setNoCacheHeaders(httpHeaders);
    httpHeaders = httpHeaders.append('Accept', 'text/html');
    const httpOptions = {
      headers: httpHeaders,
      responseType: 'text'
    };
    return firstValueFrom(this.httpClient.get(url, httpOptions)).then(response => Promise.resolve(this.getCsrf(response))).catch(error => Promise.reject(error));
  }
  /**
   * Uids oidc service
   * @returns uid
   */
  uid() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
  /**
   * Determines whether corp string is
   * @param [is_corp]
   * @returns corp string
   */
  isCorpString(is_corp) {
    return !!is_corp ? 'true' : 'false';
  }
  /**
   * Set No cache headers
   * @returns {HttpHeaders}
   */
  setNoCacheHeaders(httpHeaders) {
    httpHeaders = httpHeaders.append('x-uip', 'Web');
    httpHeaders = httpHeaders.append('x-source-id', 'Web');
    httpHeaders = httpHeaders.append('Pragma', 'no-cache');
    httpHeaders = httpHeaders.append('Cache-Control', 'no-store');
    return httpHeaders;
  }
  /**
   * Gets csrf
   * @param response
   * @returns csrf
   */
  getCsrf(response) {
    let _csrf = '';
    try {
      const el = document.createElement('html');
      el.innerHTML = response;
      const inputElements = el.getElementsByTagName('input');
      const stateEle = find(inputElements, ele => ele.getAttribute('name') === 'state');
      if (stateEle) {
        _csrf = stateEle.getAttribute('value') || '';
      }
    } catch {}
    return _csrf;
  }
  /**
   * Gets decoded access token
   * @param token
   * @returns decoded access token
   */
  getDecodedAccessToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(this.window.atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  /**
   * Gets oidc response
   * @param input
   * @returns oidc response
   */
  getOidcResponse(input) {
    const id_token = this.getParameterByName(input, 'id_token');
    if (!id_token) {
      return '';
    }
    this.localStorage.setItem('OIDC_ID_TOKEN', id_token);
    const jwt = this.getDecodedAccessToken(id_token);
    return {
      id_token: id_token,
      access_token: this.getParameterByName(input, 'access_token'),
      expires_in: this.getParameterByName(input, 'expires_in'),
      token_type: this.getParameterByName(input, 'token_type'),
      state: this.getParameterByName(input, 'state'),
      gid: get(jwt, 'sub', '')
    };
  }
  /**
   * Errors handler
   * @param error
   * @returns handler
   */
  errorHandler(error) {
    if (error && error.status === 404 && error.url && error.url.contains('id_token')) {
      const oidcResponse = this.getOidcResponse(error.url);
      if (oidcResponse) {
        return Promise.resolve(oidcResponse);
      }
    }
    let message = 'generic error';
    if (error && error.status === 401 && error.error && error.error.httpMessage === 'Unauthorized') {
      message = 'invalid username';
    }
    this.spinner.hide(SPINNER_KEYS.OVERRIDE);
    return Promise.reject(message);
  }
  /**
   * Handles error
   * @returns error
   */
  handleError() {
    return throwError(() => new Error('Failed to get token after retries'));
  }
  /**
   * Gets parameter by name
   * @param input
   * @param name
   * @returns parameter by name
   */
  getParameterByName(input, name) {
    const match = RegExp('[?&]' + name + '=([^&]*)').exec(input);
    return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
  }
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngOnDestroy() {
    // Needed for AutoSubscription
  }
  static {
    this.ɵfac = function OidcService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || OidcService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.SpinnerService), i0.ɵɵinject(i3.LocalStorageService), i0.ɵɵinject(i4.AppEnvironmentService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: OidcService,
      factory: OidcService.ɵfac,
      providedIn: 'root'
    });
  }
};
OidcService = __decorate([AutoSubscription(), __metadata("design:paramtypes", [Object, HttpClient, SpinnerService, LocalStorageService, AppEnvironmentService])], OidcService);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OidcService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: i1.HttpClient
  }, {
    type: i2.SpinnerService
  }, {
    type: i3.LocalStorageService
  }, {
    type: i4.AppEnvironmentService
  }], null);
})();

/*
 * Public API Surface of oidc
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OIDC_ENV_DEFAULTS, OidcService };
